import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { ensureValidDate } from '../../../../utils/helpers/dateUtils';

export interface CustomDatePickerProps {
  onSelectDates: (dates: Date[]) => void;
  selectedDates: Date[];
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  calendarHighlightMode?: boolean;
  availableDates?: Date[];
  disabledDates?: Date[];
}

const isSameDay = (a: Date, b: Date): boolean => {
  const normalize = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return normalize(a).getTime() === normalize(b).getTime();
};

const isPastDay = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const getDaysInMonth = (year: number, month: number): Date[] => {
  const result: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const daysFromPrevMonth = firstDay.getDay();
  const prevMonth = new Date(year, month, 0);
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    result.push(new Date(year, month - 1, prevMonth.getDate() - i));
  }

  for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
    result.push(new Date(date));
  }

  const daysFromNextMonth = 6 - lastDay.getDay();
  for (let i = 1; i <= daysFromNextMonth; i++) {
    result.push(new Date(year, month + 1, i));
  }

  return result;
};

const getDateRange = (start: Date, end: Date): Date[] => {
  const range: Date[] = [];
  let current = new Date(start);
  let endDate = new Date(end);
  if (current > endDate) [current, endDate] = [endDate, current];
  while (current <= endDate) {
    range.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return range;
};

const formatDisplayDate = (date: Date): string => {
  if (!date || isNaN(date.getTime())) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onSelectDates,
  selectedDates,
  error = false,
  helperText,
  onBlur,
  availableDates,
  disabledDates,
  calendarHighlightMode,
}) => {
  const theme = useTheme();

  const validSelectedDates = useMemo(() => {
    return selectedDates
      .map((date) => ensureValidDate(date))
      .filter((date): date is Date => date !== null);
  }, [selectedDates]);

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (validSelectedDates.length > 0) {
      const first = validSelectedDates[0];
      return new Date(first.getFullYear(), first.getMonth(), 1);
    }
    return new Date();
  });

  const [isOpen, setIsOpen] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  useEffect(() => {
    if (
      validSelectedDates.length > 0 &&
      !validSelectedDates.some(
        (date) =>
          date.getMonth() === currentMonth.getMonth() &&
          date.getFullYear() === currentMonth.getFullYear(),
      )
    ) {
      const first = validSelectedDates[0];
      setCurrentMonth(new Date(first.getFullYear(), first.getMonth(), 1));
    }
  }, [validSelectedDates, currentMonth]);

  const days = useMemo(
    () => getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth],
  );

  const handleDateSelect = (date: Date) => {
    const exists = validSelectedDates.some((d) => isSameDay(d, date));
    const updated = exists
      ? validSelectedDates.filter((d) => !isSameDay(d, date))
      : [...validSelectedDates, date];
    onSelectDates(updated);
  };

  const changeMonth = (offset: number) => {
    const next = new Date(currentMonth);
    next.setMonth(currentMonth.getMonth() + offset);
    setCurrentMonth(next);
  };

  const displayInputValue = useMemo(() => {
    return validSelectedDates.length > 0
      ? validSelectedDates.map(formatDisplayDate).filter(Boolean).join(', ')
      : '';
  }, [validSelectedDates]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Date display & toggle field */}
      <TextField
        fullWidth
        size="small"
        placeholder="Select date(s)"
        value={displayInputValue}
        onClick={() => setIsOpen((prev) => !prev)}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          sx: {
            borderRadius: '12px',
            bgcolor: 'background.paper',
            cursor: 'pointer',
            fontSize: '0.875rem',
            '& input': {
              cursor: 'pointer',
              fontWeight: 500,
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen((prev) => !prev);
                }}
                sx={{
                  color: error ? 'error.main' : 'primary.main',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <CalendarIcon size={18} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Calendar Card */}
      {isOpen && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            mt: 1.5,
            border: '1px solid',
            borderColor: error ? alpha(theme.palette.error.main, 0.4) : 'divider',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
            bgcolor: 'background.paper',
            minHeight: '260px',
          }}
        >
          {/* Left Month / Navigation Panel */}
          <Box
            sx={{
              width: { xs: '100%', sm: '36%' },
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRight: { xs: 'none', sm: '1px solid' },
              borderBottom: { xs: '1px solid', sm: 'none' },
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {currentMonth.toLocaleString('default', { month: 'long' })}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              {currentMonth.getFullYear()}
            </Typography>

            {/* Navigation buttons */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2,
                p: 0.5,
                borderRadius: '9999px',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.8),
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              }}
            >
              <IconButton
                size="small"
                onClick={() => changeMonth(-1)}
                aria-label="Previous month"
                sx={{
                  width: 28,
                  height: 28,
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                  },
                }}
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => changeMonth(1)}
                aria-label="Next month"
                sx={{
                  width: 28,
                  height: 28,
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                  },
                }}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Box>

            {/* Clear Dates Action */}
            {validSelectedDates.length > 0 && (
              <Button
                size="small"
                color="error"
                startIcon={<RotateCcw size={13} />}
                onClick={() => onSelectDates([])}
                sx={{
                  mt: 2.5,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                Clear Dates
              </Button>
            )}
          </Box>

          {/* Right Days Matrix */}
          <Box
            sx={{
              width: { xs: '100%', sm: '64%' },
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {/* Weekdays Row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
                mb: 1.25,
                textAlign: 'center',
              }}
            >
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Typography
                  key={day}
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    userSelect: 'none',
                  }}
                >
                  {day}
                </Typography>
              ))}
            </Box>

            {/* Days Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
                justifyItems: 'center',
              }}
            >
              {days.map((day) => {
                const isSelected = validSelectedDates.some((d) => isSameDay(d, day));
                const isInDragRange =
                  dragStartDate && hoveredDate
                    ? getDateRange(dragStartDate, hoveredDate).some((d) => isSameDay(d, day))
                    : false;

                let isDisabled = false;
                let isAvailable = true;
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                if (isPastDay(day) || !isCurrentMonth) {
                  isDisabled = true;
                  isAvailable = false;
                }

                if (calendarHighlightMode && isCurrentMonth) {
                  if (disabledDates && disabledDates.some((d) => isSameDay(d, day))) {
                    isDisabled = true;
                    isAvailable = false;
                  } else if (availableDates && !availableDates.some((d) => isSameDay(d, day))) {
                    isAvailable = false;
                    isDisabled = true;
                  }
                }

                return (
                  <ButtonBase
                    key={day.toISOString()}
                    disabled={isDisabled}
                    onMouseDown={(e) => {
                      if (isDisabled) return;
                      if (e.shiftKey) {
                        setIsDragging(true);
                        setDragStartDate(day);
                        setHoveredDate(day);
                      } else {
                        handleDateSelect(day);
                      }
                    }}
                    onMouseEnter={() => {
                      if (isDragging) setHoveredDate(day);
                    }}
                    onMouseUp={() => {
                      if (isDisabled) return;
                      if (isDragging && dragStartDate && hoveredDate) {
                        const range = getDateRange(dragStartDate, hoveredDate);
                        const updated = [
                          ...validSelectedDates,
                          ...range.filter(
                            (date) => !validSelectedDates.some((d) => isSameDay(d, date)),
                          ),
                        ];
                        onSelectDates(updated);
                      }
                      setIsDragging(false);
                      setDragStartDate(null);
                      setHoveredDate(null);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      fontSize: '0.8125rem',
                      fontWeight: isSelected ? 700 : 500,
                      position: 'relative',
                      transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: isDisabled
                        ? 'text.disabled'
                        : isSelected
                          ? '#ffffff'
                          : isInDragRange
                            ? 'primary.dark'
                            : 'text.primary',
                      bgcolor: isSelected
                        ? 'primary.main'
                        : isInDragRange
                          ? alpha(theme.palette.primary.main, 0.14)
                          : calendarHighlightMode && isAvailable && !isDisabled
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                      boxShadow: isSelected
                        ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`
                        : 'none',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      ...(isDisabled && {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 14,
                          height: 1,
                          bgcolor: 'divider',
                          transform: 'translate(-50%, -50%) rotate(45deg)',
                        },
                      }),
                      ...(!isDisabled &&
                        !isSelected && {
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            transform: 'scale(1.05)',
                          },
                        }),
                    }}
                  >
                    {day.getDate()}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

